import { Checkbox, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { FormikProps } from "formik";
import CustomTextArea from "../../../../../../../../../../../../components/CustomTextArea";
import Flex from "../../../../../../../../../../../../components/Flex";
import OutlineCard from "../../../../../../../../../../../../components/OutlineCard";
import Text from "../../../../../../../../../../../../components/Text";
import StyledButtonV2 from "../../../../../../../../../../../../components/app/StyledButtonV2";
import StyledFormModal from "../../../../../../../../../../../../components/globalModal/StyledModal";
import { DisclosureHost } from "../../../../../../../../../../../../hooks/use-disclosure";
import useFormDraft from "../../../../../../../../../../../../hooks/use-form-draft";
import { Form2Payload } from "../../../../../../../../../scheduling/appointment/encounter/encounter-constant";
import {
  MseOption,
  PROGRESS_RISK_ACT_FIELDS,
  PROGRESS_RISK_ACT_OPTIONS,
  PROGRESS_RISK_ASSESSMENT_FIELDS,
  PROGRESS_RISK_LEVEL_OPTIONS,
} from "../ProgressNoteConstant";

interface ProgressRiskAssessmentModalProps {
  disclosureHost: DisclosureHost;
  formik: FormikProps<Form2Payload>;
}

interface PillRadioRowProps {
  label: string;
  options: readonly MseOption[];
  value: string;
  onChange: (value: string) => void;
}

/**
 * One labeled row of pill radios — the label sits on the left and every option
 * is its own outlined chip, so the four rows line up as a single block.
 */
const PillRadioRow = ({
  label,
  options,
  value,
  onChange,
}: PillRadioRowProps) => (
  <Flex alignItems="center" gap={1} flexWrap="wrap" width="100%">
    <Text fontSize="14px" fontWeight="bold" sx={{ minWidth: "150px" }}>
      {label}
    </Text>
    <RadioGroup
      row
      value={value}
      onChange={(_, selected) => onChange(selected)}
      sx={{ gap: 1 }}
    >
      {options.map((option) => {
        const selected = value === option.value;
        return (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={<Radio size="small" />}
            label={<Text fontSize="14px">{option.label}</Text>}
            sx={{
              m: 0,
              pr: 1.5,
              borderRadius: "6px",
              border: "1px solid",
              borderColor: selected ? "primary.main" : "#D0D5DD",
              backgroundColor: selected ? "#F0F6FB" : "transparent",
            }}
          />
        );
      })}
    </RadioGroup>
  </Flex>
);

/**
 * Progress Note Risk Assessment — the "denies all areas of risk" flag above a
 * card holding the level of risk, the three act questions and the three
 * free-text blocks.
 *
 * The Psychotherapy Note risk modal captures a single risk level plus a note, so
 * this section uses its own keys rather than sharing `riskLevel` /
 * `riskLevelNote` with it.
 */
const ProgressRiskAssessmentModal = ({
  disclosureHost,
  formik,
}: ProgressRiskAssessmentModalProps) => {
  // Edits stay in the draft until Save, so the note behind the modal is
  // untouched while the user is still filling it in.
  const draft = useFormDraft(
    formik,
    PROGRESS_RISK_ASSESSMENT_FIELDS,
    disclosureHost.opened
  );

  const values = draft.values;

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={() => disclosureHost.close()}
      dialogTitle="Risk Assessment"
      dialogAction={
        <>
          <StyledButtonV2
            label="Cancel"
            variant="outlined"
            onClick={() => disclosureHost.close()}
          />
          <StyledButtonV2
            label="Save"
            variant="contained"
            onClick={() => {
              draft.save();
              disclosureHost.close();
            }}
            sx={{ width: "105px" }}
          />
        </>
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "60vw",
          height: "80vh",
        },
      }}
    >
      <Flex flexDirection="column" gap={2} width="100%" pt={2}>
        <FormControlLabel
          sx={{ mr: 0 }}
          control={
            <Checkbox
              sx={{ py: 0.25 }}
              name="riskDeniesAllAreas"
              checked={!!values?.riskDeniesAllAreas}
              onChange={(_, checked) =>
                draft.setFieldValue("riskDeniesAllAreas", checked)
              }
            />
          }
          label={
            <Text fontSize="14px">
              Patient denies all areas of risk.{" "}
              <Text component="span" fontSize="14px" color="primary">
                No contrary clinical indications present.
              </Text>
            </Text>
          }
        />

        <OutlineCard sx={{ padding: "16px" }}>
          <Flex flexDirection="column" gap={1.5} width="100%">
            <PillRadioRow
              label="Level of Risk"
              options={PROGRESS_RISK_LEVEL_OPTIONS}
              value={values?.riskLevelOfRisk ?? ""}
              onChange={(value) =>
                draft.setFieldValue("riskLevelOfRisk", value)
              }
            />

            {PROGRESS_RISK_ACT_FIELDS.map((field) => (
              <PillRadioRow
                key={field.key}
                label={field.label}
                options={PROGRESS_RISK_ACT_OPTIONS}
                value={(values?.[field.key] as string) ?? ""}
                onChange={(value) => draft.setFieldValue(field.key, value)}
              />
            ))}

            {/* The three free-text blocks carry their label as the placeholder,
                so the card reads as one continuous form rather than a stack of
                separately-labeled fields. */}
            <CustomTextArea
              name="riskFactors"
              placeholder="Risk Factors"
              value={values?.riskFactors || ""}
              onChange={draft.handleChange}
              minRows={3}
            />
            <CustomTextArea
              name="riskProtectiveFactors"
              placeholder="Protective Factors"
              value={values?.riskProtectiveFactors || ""}
              onChange={draft.handleChange}
              minRows={3}
            />
            <CustomTextArea
              name="riskAdditionalDetails"
              placeholder="Additional Details"
              value={values?.riskAdditionalDetails || ""}
              onChange={draft.handleChange}
              minRows={3}
            />
          </Flex>
        </OutlineCard>
      </Flex>
    </StyledFormModal>
  );
};

export default ProgressRiskAssessmentModal;
